import React, { Fragment, useMemo } from 'react';

import DateTimePicker from 'react-datetime-picker';
import { Button, Col, Row } from 'react-bootstrap';
import 'react-datetime-picker/dist/DateTimePicker.css';
import 'react-calendar/dist/Calendar.css';
import 'react-clock/dist/Clock.css';

import {
    ImageLeadField,
    InputField,
    TextAreaEditorField,
    TextAreaField,
    CheckboxField,
} from '../../blocks/edit_blocks';
import MyMailings from '../../blocks/MyMailings';
import GameSearchSelect from '../../components/GameSearchSelect';
import StorySearchSelect from '../../components/StorySearchSelect';
import SelectSearch from '../../tools/search_selector';

const EMPTY_ARR = [];

function formatDate(date) {
    return date ? new Date(date) : null;
}

export default function PocketForm({
    fieldList, invalidFields, currentItem, currentLang,
    onChange, onLangDataChange, onDateChange, onImageChange,
    onSearch, direction, withClear, onClear,
}) {
    const form = useMemo(() => {
        const elements = [];

        fieldList.map((field) => {
            const label = field.isMultilang ? `${field.label} (${currentLang})` : field.label;
            const multilangValue = currentItem.content?.[currentLang]?.[field.name];
            const value = (field.isMultilang ? multilangValue : currentItem[field.name]) || '';
            const isInvalid = field.isMultilang ? invalidFields[currentLang]?.includes(field.name) : invalidFields.multi?.includes(field.name);
            const handleFieldChange = field.isMultilang ? onLangDataChange : onChange;
            const handleImageChange = (e) => onImageChange({ id: e.currentTarget.id, value: e.currentTarget.value, isMultilang: field.isMultilang, fileName: e.currentTarget.fileName });

            if (field.type === 'input') {
                elements.push(
                    <InputField
                        key={field.name}
                        name={field.name}
                        label={label}
                        value={value}
                        onChange={handleFieldChange}
                        maxLength={field.maxLength}
                        required={field.isRequired}
                        is_not_valid={isInvalid}
                    />,
                );
            } else if (field.type === 'textarea') {
                elements.push(
                    <TextAreaField
                        key={field.name}
                        name={field.name}
                        label={label}
                        value={value}
                        onChange={handleFieldChange}
                        maxLength={field.maxLength}
                        rows={field.textareaRows}
                        required={field.isRequired}
                        is_not_valid={isInvalid}
                    />,
                );
            } else if (field.type === 'textarea-editor') {
                elements.push(
                    <TextAreaEditorField
                        key={field.name}
                        name={field.name}
                        label={label}
                        value={value}
                        onChange={handleFieldChange}
                        maxLength={field.maxLength}
                        rows={field.textareaRows}
                        required={field.isRequired}
                        is_not_valid={isInvalid}
                    />,
                );
            } else if (field.type === 'date') {
                elements.push(
                    <Fragment key={field.name}>
                        <label className="form-label">
                            <b>{label}</b>
                            {field.isRequired && <span style={{ color: 'red' }}>*</span>}
                        </label>
                        <DateTimePicker
                            className={isInvalid ? 'is_error' : ''}
                            id={`date-select-${field.name}`}
                            value={formatDate(value)}
                            onChange={(date) => onDateChange(field.name, date)}
                            locale="ru-ru"
                            showLeadingZeros
                            format="dd.MM.yyyy"
                            disableClock
                        />
                    </Fragment>,
                );
            } else if (field.type === 'date-time') {
                elements.push(
                    <Fragment key={field.name}>
                        <label className="form-label">
                            <b>{label}</b>
                            {field.isRequired && <span style={{ color: 'red' }}>*</span>}
                        </label>
                        <DateTimePicker
                            className={isInvalid ? 'is_error' : ''}
                            id={`date-select-${field.name}`}
                            value={formatDate(value)}
                            onChange={(date) => onDateChange(field.name, date)}
                            locale="ru-ru"
                            showLeadingZeros
                            format="dd.MM.yyyy HH:mm"
                            disableClock
                        />
                    </Fragment>,
                );
            } else if (field.type === 'image') {
                elements.push(
                    <ImageLeadField
                        key={field.name}
                        name={field.name}
                        label={label}
                        value={value}
                        onChange={handleImageChange}
                        minSize={field.imageMinSize}
                        restrictions={`Разрешение не менее ${field.imageMinSize}. Формат: jpeg, png, webp.`}
                        required={field.isRequired}
                        is_not_valid={isInvalid}
                    />,
                );
            } else if (field.type === 'checkbox') {
                elements.push(
                    <CheckboxField
                        type="checkbox"
                        key={field.name}
                        className="mb-3"
                        name={field.name}
                        label={label}
                        value={value}
                        onChange={handleFieldChange}
                    />,
                );
            } else if (field.type === 'select-search') {
                elements.push(
                    <Fragment key={field.name}>
                        <label className="form-label">
                            <b>{label}</b>
                            {field.isRequired && <span style={{ color: 'red' }}>*</span>}
                        </label>
                        <SelectSearch
                            className={isInvalid ? 'is_error' : ''}
                            isMulti
                            excludes={EMPTY_ARR}
                            id={field.name}
                            name={field.name}
                            staticData={field.staticSelectData}
                            default_values={value}
                            searchValue={value}
                            onChange={handleFieldChange}
                        />
                    </Fragment>,
                );
            } else if (field.type === 'select') {
                elements.push(
                    <Fragment key={field.name}>
                        <label className="form-label">
                            <b>{label}</b>
                            {field.isRequired && <span style={{ color: 'red' }}>*</span>}
                        </label>
                        <SelectSearch
                            className={isInvalid ? 'is_error' : ''}
                            isSingle
                            excludes={EMPTY_ARR}
                            id={field.name}
                            name={field.name}
                            default_value={value}
                            staticData={field.staticSelectData}
                            onChange={handleFieldChange}
                        />
                    </Fragment>,
                );
            } else if (field.type === 'gameSearch') {
                elements.push(
                    <GameSearchSelect
                        key={field.name}
                        currentTitle={currentItem.title || currentItem.entity?.name}
                        onChange={onSearch}
                    />,
                );
            } else if (field.type === 'storySearch') {
                elements.push(
                    <StorySearchSelect
                        key={field.name}
                        currentAlias={currentItem.alias}
                        currentTitle={currentItem.title || currentItem.entity?.content?.[0]?.title}
                        onChange={onSearch}
                    />,
                );
            } else if (field.type === 'mailing-list') {
                elements.push(
                    <Row className="mt-4">
                        <Col md={7}>
                            <MyMailings newMailing={field.newMailing} project={field.project} />
                        </Col>
                    </Row>,
                );
            }
        });

        return direction === 'column' ? (
            <Row>
                <Col md={12}>
                    {elements}
                </Col>
            </Row>
        ) : (
            <>
                {elements.map((element) => (
                    <Col key={element.key} md={12 / elements.length - (withClear ? 1 : 0)}>
                        {element}
                    </Col>
                ))}
                {withClear && (
                    <Col md={2} style={{ textAlign: 'right' }}>
                        <Button variant="outline-secondary" style={{ marginTop: '31px' }} onClick={onClear}>
                            Сбросить
                        </Button>
                    </Col>
                )}
            </>
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldList, invalidFields, currentItem, currentLang]);

    return form;
}

PocketForm.defaultProps = {
    invalidFields: {},
    direction: 'column',
};
